/**
 * src/components/AirDatePicker.js — Airbnb-style date picker.
 *
 * A field that opens a white, rounded calendar card with two months side by
 * side (one on narrow screens): round day buttons, the chosen day(s) filled
 * black, the days of a range on a soft grey band, past days greyed out.
 *
 *   <AirDatePicker label="Required by" value="2026-11-20" onChange={setDate} />
 *   <AirDatePicker range labels={['Check-in', 'Check-out']}
 *                  value={[checkIn, checkOut]} onChange={([a, b]) => …} />
 *   <AirDatePicker range bar min="" labels={['From', 'To']} … />   // one-line, 40 px, for filter bars
 *
 * Values are 'YYYY-MM-DD' strings ('' = not set). The card is portalled to
 * <body> and fully inline-styled, so it works inside scrolling dialogs and
 * outside an `.air` root. The same file lives in the public app.
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react';

const INK = '#222222';
const MUTED = '#6a6a6a';
const FAINT = '#b0b0b0';
const BAND = '#f7f7f7';
const SANS = "'Jost', -apple-system, BlinkMacSystemFont, sans-serif";

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const pad = (n) => String(n).padStart(2, '0');
const iso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const todayIso = () => { const t = new Date(); return iso(t.getFullYear(), t.getMonth(), t.getDate()); };
const parse = (s) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || ''); return m ? { y: +m[1], m: +m[2] - 1 } : null; };
export const formatDay = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
  if (!m) return s || '';
  return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};
const monthName = (y, m) => new Date(y, m, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
// "Check-in" → "check-in date"; a label that already says "date" is kept as is.
const dateWord = (l) => (/date$/i.test(l) ? l.toLowerCase() : `${l.toLowerCase()} date`);
const shift = ({ y, m }, by) => { const d = new Date(y, m + by, 1); return { y: d.getFullYear(), m: d.getMonth() }; };

const Month = ({ y, m, start, end, hover, min, onPick, onHover, range }) => {
  const first = new Date(y, m, 1).getDay();
  const days = new Date(y, m + 1, 0).getDate();
  const cells = [...Array(first).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  const bandEnd = end || (range && start && hover > start ? hover : '');
  return (
    <div style={{ width: 308 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 44px)', rowGap: 2 }}>
        {WEEKDAYS.map((w, i) => <div key={i} style={{ textAlign: 'center', fontSize: 12, fontWeight: 600, color: MUTED, padding: '0 0 10px' }}>{w}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={`b${i}`} />;
          const day = iso(y, m, d);
          const off = min && day < min;
          const edge = day === start || day === end;
          const inBand = range && start && bandEnd && day > start && day < bandEnd;
          const bandLeft = range && start && bandEnd && day === start && bandEnd > start;
          const bandRight = range && start && bandEnd && day === bandEnd && bandEnd > start;
          return (
            <div key={day} style={{
              position: 'relative', height: 44,
              background: inBand ? BAND : bandLeft ? `linear-gradient(90deg, transparent 50%, ${BAND} 50%)` : bandRight ? `linear-gradient(90deg, ${BAND} 50%, transparent 50%)` : 'transparent',
            }}>
              <button type="button" disabled={off} onClick={() => onPick(day)} onMouseEnter={() => onHover(day)}
                aria-label={formatDay(day)} aria-pressed={edge}
                style={{
                  width: 44, height: 44, borderRadius: '50%', border: 'none', padding: 0, cursor: off ? 'default' : 'pointer',
                  fontFamily: SANS, fontSize: 14, fontWeight: 600,
                  background: edge ? INK : 'transparent', color: edge ? '#fff' : off ? '#dddddd' : INK,
                  boxShadow: !edge && !off && day === hover ? `inset 0 0 0 1.5px ${INK}` : 'none',
                  textDecoration: off ? 'line-through' : 'none',
                }}>
                {d}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** The two-month card. */
const Calendar = ({ range, start, end, min, onPick, view, setView, twoUp }) => {
  const [hover, setHover] = useState('');
  const second = shift(view, 1);
  const header = (v, left, right) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 32, marginBottom: 14 }}>
      <span style={{ width: 32 }}>{left}</span>
      <span style={{ fontFamily: SANS, fontSize: 16, fontWeight: 600, color: INK }}>{monthName(v.y, v.m)}</span>
      <span style={{ width: 32, display: 'flex', justifyContent: 'flex-end' }}>{right}</span>
    </div>
  );
  const nowMonth = parse(todayIso());
  const atStart = min && view.y * 12 + view.m <= nowMonth.y * 12 + nowMonth.m;
  const arrow = (dir, disabled) => (
    <button type="button" aria-label={dir < 0 ? 'Previous month' : 'Next month'} disabled={disabled} onClick={() => setView(shift(view, dir))}
      style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: 'transparent', cursor: disabled ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: disabled ? '#dddddd' : INK }}>
      {dir < 0 ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
    </button>
  );
  const props = { start, end, hover, min, onPick, onHover: setHover, range };
  return (
    <div style={{ display: 'flex', gap: 40 }} onMouseLeave={() => setHover('')}>
      <div>
        {header(view, arrow(-1, atStart), twoUp ? null : arrow(1, false))}
        <Month y={view.y} m={view.m} {...props} />
      </div>
      {twoUp && (
        <div>
          {header(second, null, arrow(1, false))}
          <Month y={second.y} m={second.m} {...props} />
        </div>
      )}
    </div>
  );
};

/**
 * @param {object}   p
 * @param {boolean}  [p.range]        pick a start and an end date
 * @param {string|string[]} p.value   'YYYY-MM-DD', or [start, end] when range
 * @param {Function} p.onChange       gets the same shape as value
 * @param {string}   [p.label]        single mode label
 * @param {string[]} [p.labels]       range mode labels
 * @param {string}   [p.min]          earliest day; default today ('' = no limit)
 * @param {string}   [p.placeholder]
 * @param {boolean}  [p.bar]          one-line 40 px field (filter bars): "From – To" with a clear button
 * @param {boolean}  [p.nights]       range card title counts nights (stays); false = shows the dates
 */
export default function AirDatePicker({ range = false, value, onChange, label = 'Date', labels = ['Start', 'End'], min, placeholder = 'Add date', compact = false, bar = false, nights = true }) {
  const [start, end] = range ? (value || ['', '']) : [value || '', ''];
  const floor = min === undefined ? todayIso() : min;
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => parse(start) || parse(todayIso()));
  const [pos, setPos] = useState(null);
  const [twoUp, setTwoUp] = useState(true);
  const anchor = useRef(null);
  const card = useRef(null);

  // Place the card under the field (above it when there's no room), inside the viewport.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const r = anchor.current?.getBoundingClientRect();
      if (!r) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const two = vw >= 760;
      const w = two ? 32 + 308 * 2 + 40 + 32 : Math.min(vw - 16, 32 + 308 + 32);
      const h = 470;
      const left = Math.max(8, Math.min(r.left, vw - w - 8));
      const below = r.bottom + 8;
      const top = below + h > vh && r.top - h - 8 > 8 ? r.top - h - 8 : below;
      setTwoUp(two);
      setPos({ left, top, width: w });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!card.current?.contains(e.target) && !anchor.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } }; // keep a surrounding dialog open
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  const openAt = () => { setView(parse(start) || parse(floor || todayIso()) || parse(todayIso())); setOpen(true); };

  const pick = (day) => {
    if (!range) { onChange(day); setOpen(false); return; }
    if (!start || end || day < start) onChange([day, '']);
    else { onChange([start, day]); setOpen(false); }
  };
  const clear = () => onChange(range ? ['', ''] : '');

  const segment = (title, val, key) => (
    <div key={key} style={{ flex: 1, minWidth: 0, padding: compact ? '8px 12px' : '10px 14px', borderLeft: key === 1 ? '1px solid #b0b0b0' : 'none' }}>
      <div style={{ fontFamily: SANS, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: INK }}>{title}</div>
      <div style={{ fontFamily: SANS, fontSize: 14, color: val ? INK : MUTED, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 2 }}>{val ? formatDay(val) : placeholder}</div>
    </div>
  );

  const summary = useMemo(() => {
    if (!range || !start || !end) return '';
    if (!nights) return `${formatDay(start)} – ${formatDay(end)}`;
    const n = Math.round((new Date(end) - new Date(start)) / 86400000);
    return `${n} night${n === 1 ? '' : 's'}`;
  }, [range, start, end, nights]);

  // One-line field for filter bars: [📅 From – To  ×], same height as the selects beside it.
  const barField = () => {
    const part = (val, title) => (
      <span style={{ fontFamily: SANS, fontSize: 14, color: val ? INK : MUTED, whiteSpace: 'nowrap' }}>{val ? formatDay(val) : title}</span>
    );
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 38, padding: '0 12px' }}>
        <CalendarDays size={15} style={{ color: MUTED, flexShrink: 0 }} />
        {part(start, range ? labels[0] : label)}
        {range && <span style={{ color: MUTED, fontSize: 14 }}>–</span>}
        {range && part(end, labels[1])}
        {start && (
          <span role="button" tabIndex={0} aria-label="Clear dates"
            onClick={(e) => { e.stopPropagation(); clear(); setOpen(false); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); clear(); } }}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: '50%', background: '#ebebeb', color: INK, marginLeft: 2 }}>
            <X size={12} />
          </span>
        )}
      </span>
    );
  };

  return (
    <>
      <button ref={anchor} type="button" onClick={() => (open ? setOpen(false) : openAt())} aria-haspopup="dialog" aria-expanded={open}
        style={{
          display: 'flex', width: bar ? 'auto' : '100%', padding: 0, textAlign: 'left', cursor: 'pointer', background: '#fff',
          border: `1px solid ${open ? INK : FAINT}`, borderRadius: 8, boxShadow: open ? `0 0 0 1px ${INK}` : 'none',
        }}>
        {bar ? barField() : range ? [segment(labels[0], start, 0), segment(labels[1], end, 1)] : segment(label, start, 0)}
      </button>
      {open && pos && createPortal(
        <div ref={card} role="dialog" aria-label={range ? `${labels[0]} and ${labels[1]}` : label}
          style={{
            position: 'fixed', left: pos.left, top: pos.top, width: pos.width, boxSizing: 'border-box', zIndex: 2000,
            background: '#fff', borderRadius: 32, boxShadow: '0 6px 20px rgba(0,0,0,0.2)', padding: '24px 32px 20px', fontFamily: SANS,
          }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 600, color: INK }}>{range ? (summary || `Select ${dateWord(start ? labels[1] : labels[0])}`) : label}</div>
              <div style={{ fontSize: 14, color: MUTED, marginTop: 2 }}>
                {range ? (start ? `${formatDay(start)}${end ? ` – ${formatDay(end)}` : ''}` : 'Pick the first day') : (start ? formatDay(start) : 'Pick a day')}
              </div>
            </div>
          </div>
          <Calendar range={range} start={start} end={end} min={floor} onPick={pick} view={view} setView={setView} twoUp={twoUp} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <button type="button" onClick={clear} disabled={!start}
              style={{ background: 'none', border: 'none', padding: '8px 0', cursor: start ? 'pointer' : 'default', fontFamily: SANS, fontSize: 14, fontWeight: 600, color: start ? INK : FAINT, textDecoration: 'underline', textUnderlineOffset: 3 }}>
              Clear dates
            </button>
            <button type="button" onClick={() => setOpen(false)}
              style={{ background: INK, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', cursor: 'pointer', fontFamily: SANS, fontSize: 14, fontWeight: 600 }}>
              Close
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
